
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  CreditCard,
  IndianRupee,
  Loader2,
  Plus,
  RefreshCw,
  Users,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useSchool } from '../../../context/SchoolContext';
import { routeConstants } from '../../../constants/routeConstants';
import { academicApi } from '../../academics/services/academicApi';
import FeeReceiptModal from '../components/FeeReceiptModal';
import {
  feesApi,
  getFeesApiErrorMessage,
} from '../services/feesApi';

const money = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', {
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
};

const FeeManagementPage = () => {
  const navigate = useNavigate();
  const { schoolId, activeAcademicYear, activeAcademicYearId } = useSchool();
  const [classLevels, setClassLevels] = useState([]);
  const [sections, setSections] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);

  const [filters, setFilters] = useState({
    academic_year_id: activeAcademicYearId || '',
    class_level_id: '',
    section_id: '',
    status: '',
  });

  const [dashboard, setDashboard] = useState({
    summary: {},
    class_wise: [],
    monthly_collection: [],
    payment_mode_summary: [],
    recent_payments: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [receiptId, setReceiptId] = useState(null);

  useEffect(() => {
    setFilters((current) => ({
      ...current,
      academic_year_id: activeAcademicYearId || '',
      class_level_id: '',
      section_id: '',
    }));
    setClassLevels([]);
    setSections([]);

    if (!schoolId || !activeAcademicYearId) return;

    setLoadingClasses(true);
    academicApi
      .getClassLevels(schoolId, {
        academicYearId: activeAcademicYearId,
        status: 'ACTIVE',
      })
      .then((rows) => setClassLevels(rows || []))
      .catch((requestError) =>
        setError(getFeesApiErrorMessage(requestError, 'Unable to load classes.')),
      )
      .finally(() => setLoadingClasses(false));
  }, [schoolId, activeAcademicYearId]);

  const loadDashboard = useCallback(async () => {
    if (!schoolId) return;

    setLoading(true);
    setError('');

    try {
      const data = await feesApi.getFeeDashboard(schoolId, filters);
      setDashboard(data || {});
    } catch (requestError) {
      setError(
        getFeesApiErrorMessage(
          requestError,
          'Unable to load the fees dashboard.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [schoolId, filters]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleClassChange = async (event) => {
    const classLevelId = event.target.value;
    setFilters((current) => ({
      ...current,
      class_level_id: classLevelId,
      section_id: '',
    }));
    setSections([]);

    if (!classLevelId) return;

    setLoadingSections(true);
    try {
      const data = await academicApi.getSections(schoolId, {
        academicYearId: filters.academic_year_id,
        classLevelId,
        status: 'ACTIVE',
      });
      setSections(data || []);
    } catch (requestError) {
      setError(getFeesApiErrorMessage(requestError, 'Unable to load sections.'));
    } finally {
      setLoadingSections(false);
    }
  };

  const summary = dashboard?.summary || {};
  const cards = [
    {
      title: 'Collected',
      value: money(summary.collected_amount),
      subtitle: `${Number(summary.collection_rate || 0).toFixed(2)}% collection rate`,
      icon: ArrowUpRight,
      className: 'bg-blue-50 border-blue-100 text-blue-700',
    },
    {
      title: 'Outstanding',
      value: money(summary.outstanding_amount),
      subtitle: `${summary.pending_students || 0} pending students`,
      icon: ArrowDownRight,
      className: 'bg-orange-50 border-orange-100 text-orange-700',
    },
    {
      title: 'Overdue',
      value: money(summary.overdue_amount),
      subtitle: `${summary.overdue_students || 0} overdue students`,
      icon: AlertTriangle,
      className: 'bg-red-50 border-red-100 text-red-700',
    },
    {
      title: 'Net Fees',
      value: money(summary.net_fee_amount),
      subtitle: `${summary.total_students || 0} students`,
      icon: Users,
      className: 'bg-green-50 border-green-100 text-green-700',
    },
  ];

  const chartData = useMemo(
    () =>
      (dashboard?.class_wise || []).map((item) => ({
        class_name: item.class_name,
        collected: Number(item.collected || 0),
        pending: Number(item.pending || 0),
      })),
    [dashboard],
  );

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">
            Fees Management
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Review collections, pending balances, overdue fees, and class-wise performance.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => navigate(routeConstants.FEES_STRUCTURE)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <CreditCard className="h-4 w-4" />
            Fee Structure
          </button>
          <button
            type="button"
            onClick={() => navigate(routeConstants.FEES_COLLECTION)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Collect Fee
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">Dashboard Filters</h2>
            <p className="mt-1 text-xs text-gray-500">
              Working year comes from the global sidebar. Filter further by class and section.
            </p>
          </div>
          <button
            type="button"
            onClick={loadDashboard}
            disabled={loading}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5 text-sm font-semibold text-blue-700">
            {activeAcademicYear?.year_name || 'No working academic year'}
          </div>

          <select
            value={filters.class_level_id}
            onChange={handleClassChange}
            disabled={!filters.academic_year_id || loadingClasses}
            className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none disabled:bg-gray-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="">
              {loadingClasses
                ? 'Loading classes...'
                : activeAcademicYearId
                  ? 'All classes'
                  : 'No working year'}
            </option>
            {classLevels.map((item) => (
              <option key={item.id} value={item.id}>
                {item.class_name || item.name}
              </option>
            ))}
          </select>

          <select
            value={filters.section_id}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                section_id: event.target.value,
              }))
            }
            disabled={!filters.class_level_id || loadingSections}
            className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none disabled:bg-gray-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="">
              {loadingSections
                ? 'Loading sections...'
                : filters.class_level_id
                  ? 'All sections'
                  : 'Select class first'}
            </option>
            {sections.map((item) => (
              <option key={item.id} value={item.id}>
                {item.section_name || item.name}
              </option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={(event) =>
              setFilters((current) => ({ ...current, status: event.target.value }))
            }
            className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="">All ledger statuses</option>
            <option value="PENDING">Pending</option>
            <option value="PARTIAL">Partial</option>
            <option value="PAID">Paid</option>
          </select>

        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.title}
              className={`rounded-2xl border p-5 shadow-sm ${card.className}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium opacity-80">{card.title}</p>
                  <p className="mt-2 text-2xl font-bold">{card.value}</p>
                  <p className="mt-1 text-xs opacity-80">{card.subtitle}</p>
                </div>
                <Icon className="h-6 w-6" />
              </div>
            </div>
          );
        })}
      </div>

      <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Class-wise Collection</h2>
            <p className="mt-1 text-xs text-gray-500">Collected versus outstanding amount.</p>
          </div>
          {loading && <Loader2 className="h-5 w-5 animate-spin text-blue-600" />}
        </div>

        <div className="h-[320px]">
          {chartData.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center rounded-xl bg-gray-50 text-gray-500">
              <IndianRupee className="h-10 w-10 text-gray-300" />
              <p className="mt-3 text-sm">No fee ledger data for the selected filters.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="class_name" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                <YAxis axisLine={false} tickLine={false} />
                <Tooltip formatter={(value) => money(value)} />
                <Legend />
                <Bar dataKey="collected" name="Collected" fill="#2563eb" radius={[4, 4, 0, 0]} />
                <Bar dataKey="pending" name="Pending" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-bold text-gray-900">Recent Payments</h2>
          <p className="mt-1 text-xs text-gray-500">Latest successful collections.</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[850px] text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-5 py-3 text-left">Student</th>
                <th className="px-5 py-3 text-left">Date</th>
                <th className="px-5 py-3 text-left">Mode</th>
                <th className="px-5 py-3 text-left">Receipt</th>
                <th className="px-5 py-3 text-right">Amount</th>
                <th className="px-5 py-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {(dashboard?.recent_payments || []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-gray-500">
                    No payments found.
                  </td>
                </tr>
              ) : (
                dashboard.recent_payments.slice(0, 5).map((payment) => (
                  <tr key={payment.payment_id} className="border-t border-gray-100">
                    <td className="px-5 py-4">
                      <p className="font-semibold text-gray-900">{payment.student_name}</p>
                      <p className="mt-0.5 text-xs text-gray-500">{payment.enrolment_no}</p>
                    </td>
                    <td className="px-5 py-4 text-gray-600">{formatDate(payment.payment_date)}</td>
                    <td className="px-5 py-4 text-gray-600">{payment.payment_mode}</td>
                    <td className="px-5 py-4 text-gray-600">{payment.receipt_no || '—'}</td>
                    <td className="px-5 py-4 text-right font-semibold text-gray-900">
                      {money(payment.amount_paid)}
                    </td>
                    <td className="px-5 py-4 text-center">
                      <button
                        type="button"
                        disabled={!payment.receipt_id}
                        onClick={() => setReceiptId(payment.receipt_id)}
                        className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-400"
                      >
                        View receipt
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <FeeReceiptModal
        open={Boolean(receiptId)}
        onClose={() => setReceiptId(null)}
        schoolId={schoolId}
        receiptId={receiptId}
      />
    </div>
  );
};

export default FeeManagementPage;
